import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Stethoscope, Loader2, TrendingUp, TrendingDown, Minus,
  CheckCircle2, AlertTriangle, Target, Clock, X, Sparkles,
  RefreshCw, Download, Copy, DollarSign, Users, Zap, PieChart,
  Zap as Bolt, Send, StickyNote, Search, ChevronRight, History,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'load', label: 'Loading data channel...', icon: RefreshCw },
  { key: 'analyze', label: 'Analyze metric MTD vs bulan lepas...', icon: TrendingUp },
  { key: 'diagnose', label: 'Menulis diagnosis...', icon: Stethoscope },
  { key: 'plan', label: 'Bina action plan...', icon: Target },
];

function fmtDelta(pct) {
  if (pct == null) return { icon: Minus, color: '#9ca3af', text: '—' };
  const isFlat = Math.abs(pct) < 1;
  if (isFlat) return { icon: Minus, color: '#9ca3af', text: `${pct.toFixed(1)}%` };
  const positive = pct > 0;
  return {
    icon: positive ? TrendingUp : TrendingDown,
    color: positive ? '#10b981' : '#ef4444',
    text: `${positive ? '+' : ''}${pct.toFixed(1)}%`,
  };
}

function fmtValue(v, unit) {
  if (v == null || v === '' || Number.isNaN(v)) return '—';
  if (unit === 'RM' || unit === 'MYR') return `RM${Number(v).toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;
  if (unit === 'USD') return `$${Number(v).toLocaleString('en-MY', { maximumFractionDigits: v < 100 ? 2 : 0 })}`;
  if (unit === '%') return `${Number(v).toFixed(1)}%`;
  if (unit === 'lot' || unit === 'lots') return `${Number(v).toFixed(2)}`;
  return Number(v).toLocaleString('en-MY', { maximumFractionDigits: 2 });
}

function ProgressAnimation({ activeStep }) {
  return (
    <div className="py-10 px-6 space-y-4" data-testid="diagnosis-loading">
      <div className="flex items-center gap-3 mb-6">
        <div className="relative">
          <div className="absolute inset-0 bg-[#E63946] rounded-full blur-xl opacity-40 animate-pulse" />
          <Sparkles size={32} className="text-[#E63946] relative animate-pulse" />
        </div>
        <div>
          <div className="text-lg font-bold text-white">Diagnosing Channel</div>
          <div className="text-xs text-[hsl(var(--text-muted))]">Powered by AI — analisa dalam Bahasa Melayu</div>
        </div>
      </div>
      <div className="space-y-3">
        {STEPS.map((s, idx) => {
          const active = idx === activeStep;
          const done = idx < activeStep;
          const Icon = s.icon;
          return (
            <div key={s.key} className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
              done ? 'bg-[#10b981]/10 border-[#10b981]/40'
                : active ? 'bg-[#E63946]/10 border-[#E63946]/40 scale-[1.02] shadow-lg shadow-[#E63946]/10'
                : 'bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                done ? 'bg-[#10b981]' : active ? 'bg-[#E63946]' : 'bg-[hsl(var(--surface-overlay))]'
              }`}>
                {done ? <CheckCircle2 size={16} className="text-white" />
                  : active ? <Loader2 size={16} className="text-white animate-spin" />
                  : <Icon size={14} className="text-[hsl(var(--text-muted))]" />}
              </div>
              <div className={`text-sm ${active ? 'text-white font-semibold' : done ? 'text-white/80' : 'text-[hsl(var(--text-muted))]'}`}>{s.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const METRIC_GROUPS = [
  {
    title: 'Marketing Funnel',
    icon: Zap,
    color: '#8b5cf6',
    metrics: [
      { key: 'spend_myr', label: 'Ad Spend', unit: 'RM' },
      { key: 'subs', label: 'Subs (TG+Bot)', unit: '' },
      { key: 'leads_sales', label: 'Leads (Sales)', unit: '' },
      { key: 'closed', label: 'Closed', unit: '' },
      { key: 'closing_rate_pct', label: 'Closing Rate', unit: '%' },
    ],
  },
  {
    title: 'Revenue & Trading',
    icon: DollarSign,
    color: '#10b981',
    metrics: [
      { key: 'commission_myr', label: 'Commission (MYR)', unit: 'RM' },
      { key: 'deposit_usd', label: 'Deposit', unit: 'USD' },
      { key: 'active_client', label: 'Active Client', unit: '' },
      { key: 'traded_lot', label: 'Traded Lot', unit: 'lot' },
      { key: 'com_per_lot_usd', label: 'Com/Lot', unit: 'USD' },
    ],
  },
  {
    title: 'Cost Efficiency',
    icon: PieChart,
    color: '#f59e0b',
    metrics: [
      { key: 'cps_myr', label: 'CPS (Cost / Sub)', unit: 'RM', invert: true },
      { key: 'cpl_myr', label: 'CPL (Cost / Lead)', unit: 'RM', invert: true },
      { key: 'cpc_closed_myr', label: 'Cost / Closed', unit: 'RM', invert: true },
    ],
  },
  {
    title: 'Client Behaviour',
    icon: Users,
    color: '#06b6d4',
    metrics: [
      { key: 'avg_lot_per_client', label: 'Avg Lot / Client', unit: 'lot' },
      { key: 'avg_commission_per_client_usd', label: 'Avg Com / Client', unit: 'USD' },
    ],
  },
];

// Metrics that can be drilled down
const DRILL_DOWN_METRICS = new Set([
  'commission_myr', 'commission_usd', 'deposit_usd', 'active_client',
  'traded_lot', 'spend_myr', 'subs', 'leads_sales', 'closed',
]);

function MetricPill({ metric, cur, prev, delta, onDrillDown }) {
  const d = fmtDelta(delta);
  let dColor = d.color;
  if (metric.invert && delta != null && Math.abs(delta) >= 1) {
    dColor = delta > 0 ? '#ef4444' : '#10b981';
  }
  const DIcon = d.icon;
  const drillable = DRILL_DOWN_METRICS.has(metric.key);
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] p-3 hover:border-[hsl(var(--border-strong))] transition-colors group relative">
      <div className="text-[9px] text-[hsl(var(--text-muted))] uppercase tracking-wider mb-1 truncate" title={metric.label}>{metric.label}</div>
      <div className="text-sm font-bold text-white font-mono">{fmtValue(cur, metric.unit)}</div>
      <div className="flex items-baseline justify-between mt-1">
        <div className="text-[9px] text-[hsl(var(--text-muted))] font-mono">vs {fmtValue(prev, metric.unit)}</div>
        <div className="inline-flex items-center gap-0.5 text-[10px] font-bold" style={{ color: dColor }}>
          <DIcon size={10} /> {d.text}
        </div>
      </div>
      {drillable && (
        <button
          onClick={() => onDrillDown(metric)}
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[hsl(var(--surface-overlay))] text-[hsl(var(--text-muted))] hover:text-white transition-opacity"
          title="Drill down — day-by-day + per broker"
          data-testid={`drill-${metric.key}`}
        >
          <Search size={10} />
        </button>
      )}
    </div>
  );
}

function DrillDownPanel({ ibId, period, metric, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API}/api/diagnosis/drill-down/${ibId}/${metric.key}?period=${period}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(res.data);
      } catch (e) {
        toast.error('Drill-down failed');
      } finally { setLoading(false); }
    })();
  }, [ibId, period, metric.key]);

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin text-[#E63946] mx-auto" /></div>;
  if (!data) return <div className="p-8 text-center text-xs text-[hsl(var(--text-muted))]">No data</div>;

  const max = Math.max(0.01, ...data.daily.map((d) => d.value));
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-white">{metric.label} — Day-by-Day</div>
          <div className="text-[10px] text-[hsl(var(--text-muted))]">Period {data.period} · Total: {fmtValue(data.total, metric.unit)}</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-[hsl(var(--surface-overlay))]"><X size={14} /></button>
      </div>
      {/* Bar chart */}
      <div className="flex items-end gap-1 h-32 border-b border-[hsl(var(--border))] pb-1" data-testid="drill-chart">
        {data.daily.map((d) => {
          const pct = (d.value / max) * 100;
          const day = d.date.slice(8);
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${fmtValue(d.value, metric.unit)}`}>
              <div
                className="w-full rounded-t bg-gradient-to-t from-[#E63946] to-[#f97316] hover:brightness-110 transition-all"
                style={{ height: `${Math.max(2, pct)}%` }}
              />
              <div className="text-[8px] text-[hsl(var(--text-muted))]">{day}</div>
            </div>
          );
        })}
      </div>
      {/* Table */}
      <div className="max-h-64 overflow-y-auto border border-[hsl(var(--border))] rounded-lg">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-[hsl(var(--surface-elevated))]">
            <tr className="text-[9px] uppercase text-[hsl(var(--text-muted))]">
              <th className="py-2 px-2 text-left">Date</th>
              <th className="py-2 px-2 text-right">Value</th>
              {data.broker_breakdown_supported && <th className="py-2 px-2 text-left">By Broker</th>}
            </tr>
          </thead>
          <tbody>
            {data.daily.map((d) => (
              <tr key={d.date} className="border-t border-[hsl(var(--border))]/40">
                <td className="py-1.5 px-2 text-white">{d.date}</td>
                <td className="py-1.5 px-2 text-right font-mono text-white">{fmtValue(d.value, metric.unit)}</td>
                {data.broker_breakdown_supported && (
                  <td className="py-1.5 px-2 text-[hsl(var(--text-muted))] text-[10px]">
                    {Object.entries(d.by_broker || {}).map(([b, v]) => `${b}: ${fmtValue(v, metric.unit)}`).join(' · ') || '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NotesSection({ reportId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/diagnosis/${reportId}/notes`, { headers: { Authorization: `Bearer ${token}` } });
      setNotes(res.data.notes || []);
    } catch (e) { /* silent */ }
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/diagnosis/${reportId}/notes`, { text: t }, { headers: { Authorization: `Bearer ${token}` } });
      setText('');
      toast.success('Note added');
      load();
    } catch (e) {
      toast.error('Failed to add note');
    } finally { setLoading(false); }
  };

  const removeNote = async (nid) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/diagnosis/notes/${nid}`, { headers: { Authorization: `Bearer ${token}` } });
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <section className="border border-[hsl(var(--border))] rounded-lg p-4" data-testid="diagnosis-notes">
      <div className="flex items-center gap-2 mb-3">
        <StickyNote size={14} className="text-[#E63946]" />
        <div className="text-[10px] uppercase font-bold tracking-wider text-white">Notes & Annotations</div>
      </div>
      <div className="space-y-2 mb-3 max-h-56 overflow-y-auto">
        {notes.length === 0 ? (
          <div className="text-[11px] italic text-[hsl(var(--text-muted))]">Belum ada notes — tambah context, decision, atau follow-up di sini.</div>
        ) : notes.map((n) => (
          <div key={n.id} className="text-[11px] bg-[hsl(var(--surface-elevated))] rounded p-2 group" data-testid={`note-${n.id}`}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="text-[9px] font-bold text-white">{n.created_by_name}</div>
              <div className="flex items-center gap-2">
                <div className="text-[9px] text-[hsl(var(--text-muted))]">{new Date(n.created_at).toLocaleString('en-MY', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</div>
                <button onClick={() => removeNote(n.id)} className="opacity-0 group-hover:opacity-100 text-[hsl(var(--text-muted))] hover:text-[#ef4444]"><X size={10} /></button>
              </div>
            </div>
            <div className="text-white/85 whitespace-pre-wrap">{n.text}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Tambah note atau context..."
          className="text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] min-h-[42px]"
          rows={2}
          data-testid="note-input"
        />
        <Button
          onClick={submit}
          disabled={loading || !text.trim()}
          size="sm"
          className="h-auto bg-[#E63946] hover:bg-[#c62828]"
          data-testid="note-submit"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : 'Add'}
        </Button>
      </div>
    </section>
  );
}

export default function DiagnosisReportModal({ open, onOpenChange, ibId, ibName, brandLogoUrl, existingReport, mode = 'mtd', period }) {
  const [phase, setPhase] = useState('idle');
  const [activeStep, setActiveStep] = useState(0);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [drillMetric, setDrillMetric] = useState(null);
  const jobRef = useRef(null);
  const jobKeyRef = useRef(null);
  const reportRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    if (existingReport) {
      setReport(existingReport);
      setPhase('done');
      return;
    }
    setPhase('loading');
    setActiveStep(0);
    setReport(null);
    setError('');

    // Reset the in-flight job handle when the target changes
    const jobKey = `${ibId}|${mode}|${period || ''}`;
    if (jobKeyRef.current !== jobKey) {
      jobKeyRef.current = jobKey;
      jobRef.current = null;
    }

    let step = 0;
    const stepTimer = setInterval(() => {
      step = Math.min(step + 1, STEPS.length - 1);
      setActiveStep(step);
    }, 1500);

    let cancelled = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        // Reuse an in-flight job if this effect re-runs (React StrictMode
        // double-invokes effects) so we never spawn two LLM jobs per click.
        let jobId = jobRef.current;
        if (!jobId) {
          // Synchronously mark "post in progress" BEFORE awaiting so that the
          // React StrictMode double-invocation of this effect skips the POST.
          jobRef.current = 'PENDING';
          try {
            const start = await axios.post(
              `${API}/api/diagnosis/generate/${ibId}?mode=${encodeURIComponent(mode || 'mtd')}${mode === 'month' && period ? `&period=${encodeURIComponent(period)}` : ''}`,
              {},
              { headers, timeout: 30000 }
            );
            jobId = start.data.job_id;
            jobRef.current = jobId;
          } catch (postErr) {
            jobRef.current = null;
            throw postErr;
          }
        } else if (jobId === 'PENDING') {
          // Another concurrent effect invocation is already POSTing — wait for it.
          for (let w = 0; w < 30 && jobRef.current === 'PENDING'; w++) {
            await sleep(200);
          }
          jobId = jobRef.current;
          if (!jobId || jobId === 'PENDING') throw new Error('Job tidak dapat dimulakan');
        }
        if (!jobId) throw new Error('Job tidak dapat dimulakan');

        // Poll until done / error (max ~6 minutes). Network timeouts while the
        // job is running are NOT fatal — just keep polling.
        let softFails = 0;
        for (let i = 0; i < 120 && !cancelled; i++) {
          await sleep(3000);
          if (cancelled) return;
          let res;
          try {
            res = await axios.get(`${API}/api/diagnosis/job/${jobId}`, { headers, timeout: 90000 });
          } catch (pollErr) {
            softFails += 1;
            if (softFails > 8) throw pollErr;
            continue;
          }
          softFails = 0;
          if (res.data.status === 'done' && res.data.report) {
            clearInterval(stepTimer);
            setActiveStep(STEPS.length);
            setReport(res.data.report);
            setPhase('done');
            jobRef.current = null;
            return;
          }
          if (res.data.status === 'error') {
            throw new Error(res.data.error || 'Diagnosis gagal di server');
          }
        }
        if (!cancelled) throw new Error('Diagnosis mengambil masa terlalu lama — cuba lagi');
      } catch (err) {
        if (cancelled) return;
        clearInterval(stepTimer);
        jobRef.current = null;
        const msg = err.response?.data?.detail || err.message || 'Diagnosis failed';
        setError(msg);
        setPhase('error');
        toast.error('Diagnosis gagal: ' + msg);
      }
    })();

    return () => { cancelled = true; clearInterval(stepTimer); };
  }, [open, ibId, existingReport, mode, period]);

  const handleExport = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, { backgroundColor: '#0a0a0f', scale: 2, useCORS: true, logging: false });
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `diagnosis-${(ibName || 'channel').replace(/\s+/g, '-')}-${dateStr}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Report exported as PNG');
    } catch (e) {
      toast.error('Export gagal: ' + (e.message || 'unknown error'));
    } finally { setExporting(false); }
  };

  const handleCopy = async () => {
    if (!reportRef.current) return;
    try {
      const canvas = await html2canvas(reportRef.current, { backgroundColor: '#0a0a0f', scale: 2, useCORS: true, logging: false });
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          toast.success('Copied to clipboard!');
        } catch {
          toast.error('Browser tak support clipboard image');
        }
      });
    } catch (e) {
      toast.error('Copy gagal');
    }
  };

  const handleSendToLog = (actionText) => {
    // Navigate to the IB's Log tab with prefill. LogContent picks up
    // ?prefill_action=X from the URL and opens the Add Log dialog with the
    // action text pre-filled into actionTaken + description fields.
    onOpenChange(false);
    navigate(`/ib/${ibId}/details?tab=log&prefill_action=${encodeURIComponent(actionText)}`);
  };

  const ai = report?.ai_report || {};
  const cur = report?.metrics_current || {};
  const prev = report?.metrics_previous || {};
  const cmp = report?.comparison || {};
  const proj = report?.projection_eom || {};
  const anomalies = report?.anomalies || [];
  const prevRef = report?.previous_diagnosis_ref;

  const genDate = report?.generated_at
    ? new Date(report.generated_at).toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';

  const commissionDelta = cmp?.commission_myr?.delta_pct;
  const headerBanner = (() => {
    if (commissionDelta == null || !report) return null;
    if (commissionDelta >= 5) return { color: '#10b981', label: '📈 Positive Trend', text: 'Channel tengah naik' };
    if (commissionDelta <= -10) return { color: '#ef4444', label: '⚠️ Critical Decline', text: 'Perlu intervention segera' };
    if (commissionDelta < 0) return { color: '#f59e0b', label: '⚠ Slight Decline', text: 'Monitor & optimise' };
    return { color: '#06b6d4', label: '≡ Stable', text: 'Performance kekal' };
  })();

  const commissionProj = proj?.commission_myr;
  const daysSinceLast = prevRef?.generated_at
    ? Math.floor((new Date() - new Date(prevRef.generated_at)) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto bg-[hsl(var(--background))] border-[hsl(var(--border))] p-0" data-testid="diagnosis-modal">
        <DialogTitle className="sr-only">Diagnostic Report — {ibName || report?.ib_name || ibId}</DialogTitle>
        <DialogDescription className="sr-only">AI-generated channel diagnosis report in Bahasa Melayu with observations, conclusion, and action plan.</DialogDescription>

        <div className="sticky top-0 z-10 bg-[hsl(var(--surface))]/95 backdrop-blur-md border-b border-[hsl(var(--border))] px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {brandLogoUrl ? (
              <img src={brandLogoUrl} alt={ibName} className="w-11 h-11 rounded-lg object-cover border border-[hsl(var(--border))]" />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#E63946] to-[#7c3aed] flex items-center justify-center shadow-lg shadow-[#E63946]/25">
                <Stethoscope size={20} className="text-white" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-lg font-bold text-white truncate" data-testid="diagnosis-channel-name">{ibName || report?.ib_name || ibId}</div>
              {report && (
                <div className="text-[10px] text-[hsl(var(--text-muted))] flex items-center gap-2 flex-wrap">
                  <span>MTD {report.period_current} vs {report.period_previous} (Day 1–{report.day_snapshot})</span>
                  <span>·</span>
                  <span>Generated {genDate}</span>
                  {daysSinceLast != null && (
                    <>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1 text-[#06b6d4]" data-testid="vs-last-diagnosis">
                        <History size={9} /> vs last diagnosis {daysSinceLast}d ago
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {phase === 'done' && report && (
              <>
                <button onClick={handleCopy} className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white hover:bg-[hsl(var(--surface-elevated))]" title="Copy report as image to clipboard" data-testid="diagnosis-copy">
                  <Copy size={11} /> Copy
                </button>
                <button onClick={handleExport} disabled={exporting} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-semibold bg-[#E63946] hover:bg-[#c62828] text-white shadow-lg shadow-[#E63946]/25 disabled:opacity-50" data-testid="diagnosis-export">
                  {exporting ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                  {exporting ? 'Rendering...' : 'Export PNG'}
                </button>
              </>
            )}
            <button onClick={() => onOpenChange(false)} className="p-2 rounded hover:bg-[hsl(var(--surface-overlay))]" data-testid="diagnosis-close">
              <X size={18} className="text-[hsl(var(--text-muted))]" />
            </button>
          </div>
        </div>

        {phase === 'loading' && <ProgressAnimation activeStep={activeStep} />}

        {phase === 'error' && (
          <div className="p-8 text-center">
            <AlertTriangle size={40} className="text-[#ef4444] mx-auto mb-3" />
            <div className="text-white font-bold mb-1">Diagnosis Gagal</div>
            <div className="text-xs text-[hsl(var(--text-muted))] mb-4">{error}</div>
            <Button onClick={() => onOpenChange(false)} variant="outline" size="sm">Tutup</Button>
          </div>
        )}

        {phase === 'done' && report && (
          <>
            <div ref={reportRef} className="p-6 space-y-6 bg-[hsl(var(--background))]" data-testid="diagnosis-report">
              {/* Header banner */}
              {headerBanner && (
                <div className="rounded-lg p-4 flex items-center gap-3 border" style={{ background: `${headerBanner.color}15`, borderColor: `${headerBanner.color}50` }}>
                  <div className="text-lg font-bold" style={{ color: headerBanner.color }}>{headerBanner.label}</div>
                  <div className="text-sm text-white/80">— {headerBanner.text}</div>
                  <div className="ml-auto text-right">
                    <div className="text-[10px] text-[hsl(var(--text-muted))]">Commission Δ</div>
                    <div className="text-lg font-bold" style={{ color: headerBanner.color }}>
                      {commissionDelta > 0 ? '+' : ''}{commissionDelta?.toFixed(1)}%
                    </div>
                  </div>
                </div>
              )}

              {/* Actual snapshot — the numbers being diagnosed (NOT projected) */}
              <ActualSnapshotCard cur={cur} prev={prev} cmp={cmp} report={report} />

              {/* EOM Projection (MTD mode only — otherwise projection is null) */}
              {commissionProj && commissionProj.projected_full_month > 0 && (
                <section className="border border-[#f59e0b]/30 rounded-lg p-4 bg-gradient-to-br from-[#f59e0b]/8 to-transparent" data-testid="eom-projection">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar size={14} className="text-[#f59e0b]" />
                    <div className="text-[10px] uppercase font-bold text-[#f59e0b] tracking-wider">Anggaran End-of-Month</div>
                    <div className="text-[9px] text-[hsl(var(--text-muted))] italic">Extrapolate linear (kalau trend sama sampai habis bulan)</div>
                    <div className="text-[9px] text-[hsl(var(--text-muted))] ml-auto">
                      Day {proj._meta?.mtd_day} of {proj._meta?.days_in_month} · × {proj._meta?.factor}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {['commission_myr', 'deposit_usd', 'active_client', 'traded_lot'].map((k) => {
                      const p = proj[k];
                      if (!p) return null;
                      const unit = k === 'commission_myr' ? 'RM' : k === 'deposit_usd' ? 'USD' : k === 'traded_lot' ? 'lot' : '';
                      const d = fmtDelta(p.delta_vs_prev_pct);
                      const DIcon = d.icon;
                      return (
                        <div key={k} className="rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] p-3">
                          <div className="text-[9px] uppercase text-[hsl(var(--text-muted))]">{k.replace(/_/g, ' ')}</div>
                          <div className="text-sm font-bold text-white font-mono">{fmtValue(p.projected_full_month, unit)}</div>
                          <div className="flex items-baseline justify-between text-[9px] mt-1">
                            <span className="text-[hsl(var(--text-muted))]">vs prev full: {fmtValue(p.prev_full_month, unit)}</span>
                            <span className="inline-flex items-center gap-0.5 font-bold" style={{ color: d.color }}><DIcon size={9} /> {d.text}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Anomalies */}
              {anomalies.length > 0 && (
                <section className="border border-[#ef4444]/30 rounded-lg p-4 bg-[#ef4444]/5" data-testid="anomalies">
                  <div className="flex items-center gap-2 mb-2">
                    <Bolt size={14} className="text-[#ef4444]" />
                    <div className="text-[10px] uppercase font-bold text-[#ef4444] tracking-wider">Anomalies Detected — {anomalies.length}</div>
                  </div>
                  <div className="space-y-1">
                    {anomalies.slice(0, 5).map((a, i) => (
                      <div key={i} className="text-[11px] text-white/85 flex items-center gap-2">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${a.direction === 'spike' ? 'bg-[#f59e0b]' : 'bg-[#ef4444]'}`} />
                        <span className="font-mono text-[hsl(var(--text-muted))]">{a.date}</span>
                        <span>·</span>
                        <span className="font-semibold">{a.metric}</span>
                        <span className="text-white">{a.direction === 'spike' ? '↑' : '↓'} {a.value}</span>
                        <span className="text-[hsl(var(--text-muted))]">(avg {a.mean}, {a.sigma}σ)</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Observations */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-md bg-[#E63946]/15 flex items-center justify-center">
                    <Sparkles size={13} className="text-[#E63946]" />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-white">Pemerhatian</h3>
                </div>
                <ol className="space-y-3 pl-1" data-testid="diagnosis-observations">
                  {(ai.observations || []).map((obs, i) => (
                    <li key={i} className="flex gap-3 text-sm text-white/90 leading-relaxed">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-[#E63946] to-[#c62828] text-white text-[11px] font-bold flex items-center justify-center shadow-md">{i + 1}</span>
                      <span>{obs}</span>
                    </li>
                  ))}
                </ol>
              </section>

              {/* Kesimpulan */}
              <section className="bg-gradient-to-br from-[#E63946]/10 via-[#7c3aed]/8 to-[#06b6d4]/5 border border-[#E63946]/30 rounded-lg p-5" data-testid="diagnosis-kesimpulan">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-md bg-[#E63946]/20 flex items-center justify-center">
                    <Target size={13} className="text-[#E63946]" />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-white">Kesimpulan</h3>
                </div>
                <p className="text-sm text-white leading-relaxed italic">{ai.kesimpulan}</p>
              </section>

              {/* Action Plan */}
              <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="diagnosis-action-plan">
                <ActionColumn title="What's Working" items={ai.action_plan?.whats_working} color="#10b981" icon={CheckCircle2} />
                <ActionColumn title="What's Not Working" items={ai.action_plan?.whats_not_working} color="#ef4444" icon={AlertTriangle} />
                <ActionColumn
                  title="Next 7 Days"
                  items={ai.action_plan?.next_7_days}
                  color="#f59e0b"
                  icon={Clock}
                  actionable
                  onSendToLog={handleSendToLog}
                />
              </section>

              {/* Metric groups */}
              {METRIC_GROUPS.map((group) => {
                const GroupIcon = group.icon;
                return (
                  <section key={group.title} className="border border-[hsl(var(--border))] rounded-lg p-4" style={{ background: `${group.color}05` }}>
                    <div className="flex items-center gap-2 mb-3">
                      <GroupIcon size={14} style={{ color: group.color }} />
                      <div className="text-[10px] uppercase font-bold tracking-wider" style={{ color: group.color }}>{group.title}</div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {group.metrics.map((m) => (
                        <MetricPill
                          key={m.key}
                          metric={m}
                          cur={cur[m.key]}
                          prev={prev[m.key]}
                          delta={cmp[m.key]?.delta_pct}
                          onDrillDown={setDrillMetric}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}

              {/* CSAT snapshot */}
              {report.csat_snapshot?.total_responses > 0 && (
                <section className="border border-[hsl(var(--border))] rounded-lg p-4 bg-[hsl(var(--surface-elevated))]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2 flex items-center gap-2">
                    <Users size={12} /> CSAT — {report.csat_snapshot.total_responses} responses · avg {report.csat_snapshot.avg_score}
                  </div>
                  {(report.csat_snapshot.top_negative_comments || []).map((c, i) => (
                    <div key={i} className="text-[11px] text-[#ef4444]/80 italic leading-relaxed mb-1">↳ &quot;{c}&quot;</div>
                  ))}
                </section>
              )}

              <div className="pt-2 flex items-center justify-between text-[9px] text-[hsl(var(--text-muted))] border-t border-[hsl(var(--border))]">
                <span>Generated by Channel Diagnostician · AI-powered</span>
                <span>{genDate}</span>
              </div>
            </div>

            {/* Notes — outside reportRef so it doesn't leak into exported PNG */}
            <div className="p-6 pt-0">
              <NotesSection reportId={report.id} />
            </div>
          </>
        )}

        {/* Drill-down modal */}
        {drillMetric && report && (
          <Dialog open={!!drillMetric} onOpenChange={() => setDrillMetric(null)}>
            <DialogContent className="max-w-2xl bg-[hsl(var(--background))] border-[hsl(var(--border))] p-0" data-testid="drill-modal">
              <DialogTitle className="sr-only">Drill Down — {drillMetric.label}</DialogTitle>
              <DialogDescription className="sr-only">Day-by-day and per-broker breakdown for {drillMetric.label}</DialogDescription>
              <DrillDownPanel
                ibId={ibId}
                period={report.period_current}
                metric={drillMetric}
                onClose={() => setDrillMetric(null)}
              />
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ActualSnapshotCard({ cur, prev, cmp, report }) {
  const label = report?.period_label || '';
  const items = [
    { key: 'commission_myr', label: 'Commission', unit: 'RM', accent: '#10b981' },
    { key: 'deposit_usd', label: 'Deposit', unit: 'USD', accent: '#06b6d4' },
    { key: 'active_client', label: 'Active Client', unit: '', accent: '#f59e0b' },
    { key: 'traded_lot', label: 'Traded Lot', unit: 'lot', accent: '#8b5cf6' },
  ];
  return (
    <section
      className="border border-[#E63946]/40 rounded-lg p-4 bg-gradient-to-br from-[#E63946]/10 via-[#7c3aed]/6 to-transparent"
      data-testid="actual-snapshot"
    >
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={14} className="text-[#E63946]" />
        <div className="text-[10px] uppercase font-bold text-[#E63946] tracking-wider">
          Angka Sebenar (Actual) — {report?.mode === 'last_7_days' ? 'Last 7 Days' : report?.mode === 'last_30_days' ? 'Last 30 Days' : report?.mode === 'month' ? `Bulan ${report?.period_requested || ''}` : 'MTD Sekarang'}
        </div>
        <div className="text-[9px] text-[hsl(var(--text-muted))] ml-auto truncate max-w-[50%]" title={label}>{label}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {items.map((it) => {
          const c = cur?.[it.key];
          const p = prev?.[it.key];
          const delta = cmp?.[it.key]?.delta_pct;
          const d = fmtDelta(delta);
          const DIcon = d.icon;
          return (
            <div key={it.key} className="rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] p-3">
              <div className="text-[9px] uppercase text-[hsl(var(--text-muted))] tracking-wider" style={{ color: it.accent }}>{it.label}</div>
              <div className="text-lg font-bold text-white font-mono">{fmtValue(c, it.unit)}</div>
              <div className="flex items-baseline justify-between text-[9px] mt-1">
                <span className="text-[hsl(var(--text-muted))]">vs previous: {fmtValue(p, it.unit)}</span>
                <span className="inline-flex items-center gap-0.5 font-bold" style={{ color: d.color }}><DIcon size={9} /> {d.text}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ActionColumn({ title, items, color, icon: Icon, actionable, onSendToLog }) {
  const list = items || [];
  const priorityColor = { high: '#ef4444', medium: '#f59e0b', low: '#9ca3af' };
  return (
    <div className="bg-gradient-to-br rounded-lg p-4 border" style={{
      borderColor: `${color}40`,
      background: `linear-gradient(135deg, ${color}12 0%, ${color}03 100%)`,
    }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <div className="text-[10px] uppercase font-bold tracking-wider" style={{ color }}>{title}</div>
      </div>
      <ul className="space-y-2 text-[11px] text-white/85">
        {list.length === 0 && <li className="text-[hsl(var(--text-muted))] italic">—</li>}
        {list.map((raw, i) => {
          // Support both legacy string items and new object shape
          //   { action, expected_impact, priority }
          const isObj = raw && typeof raw === 'object' && !Array.isArray(raw);
          const actionText = isObj ? (raw.action || '') : String(raw || '');
          const impact = isObj ? (raw.expected_impact || '') : '';
          const priority = isObj ? String(raw.priority || '').toLowerCase() : '';
          const pColor = priorityColor[priority] || null;
          return (
            <li key={i} className="leading-relaxed flex items-start gap-1.5 group">
              <span style={{ color }} className="mt-0.5">▸</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {pColor && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded"
                      style={{ background: `${pColor}20`, color: pColor, border: `1px solid ${pColor}40` }}
                      data-testid={`priority-${priority}`}
                    >
                      {priority}
                    </span>
                  )}
                  <span className="flex-1">{actionText}</span>
                </div>
                {impact && (
                  <div className="text-[10px] mt-1 pl-0.5 italic text-[hsl(var(--text-muted))] flex items-start gap-1">
                    <span style={{ color }} className="opacity-60">Impact:</span>
                    <span className="text-white/70">{impact}</span>
                  </div>
                )}
              </div>
              {actionable && (
                <button
                  onClick={() => onSendToLog(actionText + (impact ? ` — impact: ${impact}` : ''))}
                  className="opacity-0 group-hover:opacity-100 flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#E63946]/15 text-[#E63946] hover:bg-[#E63946]/25 transition-opacity"
                  title="Send to channel log — auto-fill new activity"
                  data-testid={`send-to-log-${i}`}
                >
                  <Send size={8} /> Log
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
